import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLang } from "../i18n";
import { api } from "../lib/apiClient";
import { STEPS, NUMERIC_FIELDS, EXPENSE_FIELDS, BOOLEAN_FIELDS } from "../config/formConfig";
import { Header } from "../components/Header";
import { Stepper } from "../components/Stepper";
import { FieldRenderer } from "../components/FieldRenderer";
import { DraftSaveDialog, DraftSavedPanel } from "../components/DraftSave";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, UploadCloud, FileCheck2, Loader2, Wallet, ShieldAlert, Save, History } from "lucide-react";

const DRAFT_KEY = "step_draft";

export default function ApplicationForm() {
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [data, setData] = useState({});
  const [files, setFiles] = useState({ facture_scolarite: null, contrat_ulaval: null });
  const [uploading, setUploading] = useState({ facture_scolarite: false, contrat_ulaval: false });
  const [attestation, setAttestation] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [duplicateError, setDuplicateError] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [draft, setDraft] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedPanel, setSavedPanel] = useState(false);
  const [resending, setResending] = useState(false);
  const [resumeCandidate, setResumeCandidate] = useState(null);
  const saveTimer = useRef(null);
  const skipAutosave = useRef(false);

  const rememberDraft = (d) => {
    if (d) localStorage.setItem(DRAFT_KEY, JSON.stringify({ token: d.token, courriel: d.courriel, updated_at: new Date().toISOString() }));
    else localStorage.removeItem(DRAFT_KEY);
  };

  const loadDraft = useCallback(async (token) => {
    try {
      const r = await api.get(`/drafts/${token}`);
      skipAutosave.current = true;
      setData(r.data.data || {});
      setFiles({ facture_scolarite: null, contrat_ulaval: null, ...(r.data.files || {}) });
      setStep(Math.min(5, Math.max(1, r.data.step || 1)));
      setDraft({ token: r.data.token, courriel: r.data.courriel });
      rememberDraft({ token: r.data.token, courriel: r.data.courriel });
      setResumeCandidate(null);
      toast.success(t("draft.resumed"));
    } catch (e) {
      localStorage.removeItem(DRAFT_KEY);
      setResumeCandidate(null);
      toast.error(t("draft.notFound"), { duration: 8000 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api.get("/settings/deposit").then((r) => {
      if (!r.data.is_open) {
        toast.error(r.data.phase === "closed" ? t("countdown.closed") : t("countdown.unscheduled"));
        navigate("/", { replace: true });
        return;
      }
      const token = searchParams.get("reprise");
      if (token) {
        setSearchParams({}, { replace: true });
        loadDraft(token);
        return;
      }
      try {
        const saved = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
        if (saved?.token) setResumeCandidate(saved);
      } catch { /* ignore */ }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const draftBody = () => ({ step, data, files });

  useEffect(() => {
    if (!draft?.token) return;
    if (skipAutosave.current) { skipAutosave.current = false; return; }
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await api.put(`/drafts/${draft.token}`, draftBody());
        setSavedAt(new Date());
      } catch { /* silent */ }
    }, 1500);
    return () => clearTimeout(saveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, files, step, draft]);

  const saveAndQuit = async (email) => {
    setSaving(true);
    try {
      let d = draft;
      if (d?.token) {
        await api.put(`/drafts/${d.token}`, draftBody());
      } else {
        const r = await api.post("/drafts", { courriel: email, lang, ...draftBody() });
        d = { token: r.data.token, courriel: r.data.courriel };
        skipAutosave.current = true;
        setDraft(d);
        rememberDraft(d);
      }
      setSavedAt(new Date());
      setSaveOpen(false);
      setSavedPanel(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur.", { duration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  const resendLink = async () => {
    if (!draft?.token) return;
    setResending(true);
    try {
      await api.post(`/drafts/${draft.token}/resend`);
      toast.success(t("draft.resent"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur.");
    } finally {
      setResending(false);
    }
  };

  const setField = (name, value) => {
    setData((d) => {
      const nd = { ...d, [name]: value };
      if (name === "faculte" && value !== "autre") nd.faculte_autre = "";
      if (name === "cycle" && value !== "autre") nd.cycle_autre = "";
      return nd;
    });
    setErrors((e) => ({ ...e, [name]: undefined }));
  };

  const totalExpenses = useMemo(
    () => EXPENSE_FIELDS.reduce((sum, k) => sum + (parseFloat(data[k]) || 0), 0),
    [data]
  );

  const validateStep = () => {
    const cfg = STEPS.find((s) => s.number === step);
    const errs = {};
    if (cfg) {
      cfg.groups.forEach((g) =>
        g.fields.forEach((f) => {
          const visible = !f.showIf || data[f.showIf.field] === f.showIf.value;
          if (visible && f.required && (!data[f.name] || String(data[f.name]).trim() === "")) {
            errs[f.name] = t("required");
          }
        })
      );
    }
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast.error(t("err.required"));
      return false;
    }
    return true;
  };

  const [checkingDup, setCheckingDup] = useState(false);

  const identityKey = useMemo(() => {
    const ni = (data.ni || "").replace(/\D/g, "");
    const email = (data.courriel || "").trim().toLowerCase();
    const tel = (data.telephone || "").replace(/\D/g, "");
    const nom = (data.nom || "").trim().toLowerCase();
    const prenom = (data.prenom || "").trim().toLowerCase();
    const parts = [];
    if (ni.length >= 8) parts.push(`ni:${ni}`);
    if (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) parts.push(`em:${email}`);
    if (nom && prenom && tel.length >= 10) parts.push(`p:${nom}|${prenom}|${tel}`);
    return parts.join(";");
  }, [data.ni, data.courriel, data.nom, data.prenom, data.telephone]);

  const checkDuplicate = useCallback(async () => {
    try {
      await api.post("/applications/check-duplicate", {
        nom: data.nom || "", prenom: data.prenom || "", ni: data.ni || "",
        courriel: data.courriel || "", telephone: data.telephone || "", lang,
      });
      setDuplicateError("");
      return true;
    } catch (e) {
      if (e.response?.status === 409) { setDuplicateError(e.response.data?.detail); return false; }
      return true;
    }
  }, [data.nom, data.prenom, data.ni, data.courriel, data.telephone, lang]);

  useEffect(() => {
    if (step !== 1) return;
    if (!identityKey) { setDuplicateError(""); return; }
    const id = setTimeout(checkDuplicate, 900);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityKey, step]);

  const next = async () => {
    if (step < 5 && !validateStep()) return;
    if (step === 1) {
      setCheckingDup(true);
      const ok = await checkDuplicate();
      setCheckingDup(false);
      if (!ok) { toast.error(t("dup.blocked"), { duration: 8000 }); window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    }
    setStep((s) => Math.min(5, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const prev = () => {
    setStep((s) => Math.max(1, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleUpload = async (key, file) => {
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error(t("step5.pdfonly"));
      return;
    }
    setUploading((u) => ({ ...u, [key]: true }));
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFiles((f) => ({ ...f, [key]: res.data }));
      toast.success(t("step5.uploaded"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload error");
    } finally {
      setUploading((u) => ({ ...u, [key]: false }));
    }
  };

  const submit = async () => {
    if (!data.lettre_presentation || !data.lettre_presentation.trim()) {
      setStep(4);
      toast.error(t("err.lettre"));
      return;
    }
    if (!files.facture_scolarite || !files.contrat_ulaval) {
      toast.error(t("err.files"));
      return;
    }
    if (!attestation) {
      toast.error(t("err.attestation"));
      return;
    }
    setSubmitting(true);
    const payload = { lang, attestation: true, ...data };
    NUMERIC_FIELDS.forEach((k) => { payload[k] = parseFloat(data[k]) || 0; });
    BOOLEAN_FIELDS.forEach((k) => { payload[k] = data[k] === undefined ? null : data[k]; });
    payload.facture_scolarite = files.facture_scolarite;
    payload.contrat_ulaval = files.contrat_ulaval;
    if (draft?.token) payload.draft_token = draft.token;
    try {
      const res = await api.post("/applications", payload);
      rememberDraft(null);
      navigate("/confirmation", { state: res.data });
    } catch (e) {
      const detail = e.response?.data?.detail || "Erreur lors de la soumission.";
      if (e.response?.status === 409) setDuplicateError(detail);
      toast.error(detail, { duration: 8000 });
    } finally {
      setSubmitting(false);
    }
  };

  const currentStep = STEPS.find((s) => s.number === step);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {resumeCandidate && !draft && (
          <div className="mb-6 rounded-xl bg-sky-50 border border-sky-200 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="draft-resume-banner">
            <History className="w-5 h-5 text-sky-600 shrink-0" />
            <p className="text-sm text-sky-900 flex-1">
              {t("draft.resumeBanner", { date: new Date(resumeCandidate.updated_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { dateStyle: "long", timeStyle: "short" }) })}
            </p>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => loadDraft(resumeCandidate.token)} data-testid="draft-resume-btn" className="bg-primary hover:bg-primary/90">{t("draft.resumeBtn")}</Button>
              <Button size="sm" variant="ghost" onClick={() => { localStorage.removeItem(DRAFT_KEY); setResumeCandidate(null); }} data-testid="draft-restart-btn">{t("draft.restartBtn")}</Button>
            </div>
          </div>
        )}

        {savedPanel ? (
          <DraftSavedPanel
            email={draft?.courriel}
            onResend={resendLink}
            resending={resending}
            onContinue={() => setSavedPanel(false)}
            onHome={() => navigate("/")}
          />
        ) : (
        <>
        <div className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-4">
            {t("step.of", { n: step })}
          </p>
          <Stepper current={step} />
        </div>

        {duplicateError && (
          <div data-testid="duplicate-error" className="mb-6 flex items-start gap-3 rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-800 animate-fade-up">
            <ShieldAlert className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">{t("dup.title")}</p>
              <p className="mt-1">{duplicateError}</p>
            </div>
          </div>
        )}

        <motion.div
          key={step}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          className="bg-card rounded-xl shadow-sm border border-slate-200/80 p-6 sm:p-8"
        >
            <h2 className="font-display text-2xl font-bold text-slate-900 mb-6">
              {lang === "fr" ? (step === 5 ? "Pièces justificatives & Soumission" : currentStep.fr) : step === 5 ? "Supporting documents & Submit" : currentStep.en}
            </h2>

            {step <= 4 &&
              currentStep.groups.map((g, gi) => (
                <div key={`${currentStep.number}-${g.fr}`} className="mb-8 last:mb-0">
                  <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide border-l-4 border-primary pl-3 mb-4">
                    {lang === "fr" ? g.fr : g.en}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
                    {g.fields.filter((f) => !f.showIf || data[f.showIf.field] === f.showIf.value).map((f) => (
                      <div key={f.name} className={f.colSpan === 2 || f.type === "yesno" ? "sm:col-span-2" : ""}>
                        <FieldRenderer field={f} value={data[f.name]} onChange={setField} error={errors[f.name]} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}

            {step === 3 && (
              <div className="mt-6 rounded-xl bg-accent/10 border border-accent/30 p-5 flex items-center justify-between" data-testid="budget-summary">
                <div className="flex items-center gap-3">
                  <span className="grid place-items-center w-10 h-10 rounded-lg bg-accent/20 text-accent">
                    <Wallet className="w-5 h-5" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{t("summary.expenses")}</p>
                    <p className="text-xs text-slate-500">{t("summary.note")}</p>
                  </div>
                </div>
                <p className="font-display text-2xl font-extrabold text-slate-900" data-testid="total-expenses">
                  {totalExpenses.toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { style: "currency", currency: "CAD" })}
                </p>
              </div>
            )}

            {step === 5 && (
              <div className="space-y-6">
                {[
                  { key: "facture_scolarite", label: t("step5.facture") },
                  { key: "contrat_ulaval", label: t("step5.contrat") },
                ].map(({ key, label }) => (
                  <div key={key} className="rounded-xl border border-slate-200 p-5" data-testid={`upload-block-${key}`}>
                    <p className="text-sm font-semibold text-slate-800 mb-1">
                      {label} <span className="text-primary">*</span>
                    </p>
                    <p className="text-xs text-slate-500 mb-3">{t("step5.pdfonly")}</p>
                    {files[key] ? (
                      <div className="flex items-center justify-between gap-4 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3">
                        <span className="flex items-center gap-2 text-sm font-medium text-emerald-800 truncate">
                          <FileCheck2 className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{files[key].original_filename}</span>
                        </span>
                        <label className="text-xs font-semibold text-primary cursor-pointer hover:underline flex-shrink-0">
                          {t("step5.replace")}
                          <input type="file" accept="application/pdf" className="hidden"
                            data-testid={`file-input-${key}`}
                            onChange={(e) => handleUpload(key, e.target.files[0])} />
                        </label>
                      </div>
                    ) : (
                      <label
                        className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-lg py-8 cursor-pointer hover:border-primary hover:bg-slate-50 transition-colors"
                        data-testid={`upload-label-${key}`}
                      >
                        {uploading[key] ? (
                          <Loader2 className="w-6 h-6 text-primary animate-spin" />
                        ) : (
                          <UploadCloud className="w-6 h-6 text-slate-400" />
                        )}
                        <span className="text-sm font-medium text-slate-600">{t("step5.upload")}</span>
                        <input type="file" accept="application/pdf" className="hidden"
                          data-testid={`file-input-${key}`}
                          onChange={(e) => handleUpload(key, e.target.files[0])} />
                      </label>
                    )}
                  </div>
                ))}

                <div className="flex items-start gap-3 rounded-xl bg-slate-50 border border-slate-200 p-4">
                  <Checkbox
                    id="attestation"
                    data-testid="attestation-checkbox"
                    checked={attestation}
                    onCheckedChange={(v) => setAttestation(!!v)}
                    className="mt-0.5"
                  />
                  <label htmlFor="attestation" className="text-sm text-slate-700 leading-relaxed cursor-pointer">
                    {t("step5.attestation")}
                  </label>
                </div>

                <p className="text-xs text-slate-500">
                  {t("step5.date")}: <span className="font-semibold text-slate-700">{new Date().toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA")}</span>
                </p>
              </div>
            )}
          </motion.div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-6">
          <Button
            variant="outline"
            onClick={prev}
            disabled={step === 1}
            data-testid="prev-btn"
            className="h-11 px-5"
          >
            <ArrowLeft className="mr-2 w-4 h-4" /> {t("btn.prev")}
          </Button>

          <div className="flex items-center gap-3 sm:mx-auto">
            <Button variant="ghost" onClick={() => (draft?.token ? saveAndQuit(draft.courriel) : setSaveOpen(true))} disabled={saving} data-testid="draft-save-btn" className="h-11 text-slate-600 hover:text-primary">
              {saving ? <Loader2 className="mr-2 w-4 h-4 animate-spin" /> : <Save className="mr-2 w-4 h-4" />} {t("draft.save")}
            </Button>
            {savedAt && (
              <span className="text-xs text-slate-400" data-testid="draft-autosaved">
                {t("draft.autosaved", { time: savedAt.toLocaleTimeString(lang === "fr" ? "fr-CA" : "en-CA", { hour: "2-digit", minute: "2-digit" }) })}
              </span>
            )}
          </div>

          {step < 5 ? (
            <Button onClick={next} disabled={checkingDup} data-testid="next-btn" className="h-11 px-6 bg-primary hover:bg-primary/90">
              {checkingDup ? <Loader2 className="mr-2 w-4 h-4 animate-spin" /> : null}{t("btn.next")} <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          ) : (
            <Button onClick={submit} disabled={submitting} data-testid="submit-btn" className="h-11 px-6 bg-primary hover:bg-primary/90">
              {submitting ? <><Loader2 className="mr-2 w-4 h-4 animate-spin" /> {t("btn.submitting")}</> : t("btn.submit")}
            </Button>
          )}
        </div>
        </>
        )}

        <DraftSaveDialog open={saveOpen} onOpenChange={setSaveOpen} defaultEmail={data.courriel} onSave={saveAndQuit} saving={saving} />
      </div>
    </div>
  );
}
